import type { DashboardChartData } from '@goldfishcode/kingmakerdata-api-sdk/libs/api/dashboard/models';
import type { PayloadAction } from '@reduxjs/toolkit';
import { has } from 'lodash-es';

import { chartValueInitialize } from './const';
import type { ChartDataType, Dashboard, SurveyChartDataType, VoterHistoryChartData } from './model';
import { Party } from './model';

export const setDashboardData = (state: Dashboard, action: PayloadAction<DashboardChartData | null>) => {
  state.dashboardChartData = action.payload;
  if (state.isFilterChartsByChart || state.isClearFilter) {
    // Reset all chart data if est_party is not in payload
    if (!Object.prototype.hasOwnProperty.call(action.payload!, 'est_party')) {
      for (const key in chartValueInitialize) {
        state[key] = chartValueInitialize[key];
      }
    }

    if (action.payload?.age_bracket) {
      // find and remove age bracket
      const datasets = Object.values(action.payload.age_bracket).filter(data => {
        if (isNaN(data)) {
          state.ageRange = data.split('-');
          return;
        }
        return data;
      });
      const labels = Object.keys(action.payload.age_bracket).filter(label => {
        return label !== 'age_range';
      });
      state.ageBracket = {
        label: [...labels],
        datasets: [...datasets],
      };
    }

    if (action.payload?.gender) {
      const genderObj = action.payload.gender;

      const genderArray = Object.keys(genderObj).map(key => {
        return [key, genderObj[key]];
      });

      const renderArray = [] as any;
      const genderValues = ['Male', 'Female'];
      const naValue = genderArray
        .filter(it => !genderValues.includes(it[0]))
        .map(it => it[1])
        .reduce((prev, next) => prev + next, 0);

      genderArray.forEach(item => {
        if (item[0] === 'Male') {
          renderArray[0] = item;
        }

        if (item[0] === 'Female') {
          const index = genderArray.length >= 3 ? 2 : 1;
          renderArray[index] = item;
        }
      });

      if (naValue > 0) renderArray[1] = ['N/A', naValue];

      const renderLabels = renderArray.map(array => array[0]);

      const renderValues = renderArray.map(array => array[1]);

      state.gender = {
        label: renderLabels,
        datasets: renderValues,
      };
    }

    if (action.payload?.est_party) {
      const estPartyObj = action.payload.est_party;

      const est_partyArray = Object.keys(estPartyObj).map(key => {
        return [key, estPartyObj[key]];
      });

      const renderArray = [] as any;

      est_partyArray.forEach(item => {
        if (item[0] === Party.STRONG_REPUBLICAN) {
          renderArray[0] = item;
        }
        if (item[0] === Party.SOFT_REPUBLICAN) {
          renderArray[1] = item;
        }

        if (item[0] === Party.SWING) {
          renderArray[2] = item;
        }

        if (item[0] === Party.SOFT_DEMOCRAT) {
          renderArray[3] = item;
        }
        if (item[0] === Party.STRONG_DEMOCRAT) {
          renderArray[4] = item;
        }
      });

      const renderLabels = renderArray.map(array => array[0]);
      const renderValues = renderArray.map(array => array[1]);

      state.est_party = {
        label: renderLabels,
        datasets: renderValues,
      };
    }

    if (action.payload?.contact_bar && action.payload?.contact_poll) {
      const contactPool = action.payload.contact_poll;
      let count = 0;
      const contactPoolKeys = Object.keys(contactPool);
      contactPoolKeys.forEach(key => {
        if (contactPool[key] === 0) {
          count++;
        }
      });

      if (count !== contactPoolKeys.length) {
        state.contact_bar = {
          label: Object.keys(action.payload.contact_bar),
          datasets: Object.values(action.payload.contact_bar),
        };
      } else {
        state.contact_bar = undefined;
      }
    }

    if (action.payload?.contact_poll) {
      const contactPollData = action.payload.contact_poll;
      const contactPollLabel = Object.keys(contactPollData).reverse();
      const contactPollDatasets = Object.values(contactPollData).reverse();

      let count = 0;
      const contactPoolKeys = Object.keys(contactPollData);
      contactPoolKeys.forEach(key => {
        if (contactPollData[key] === 0) {
          count++;
        }
      });

      if (count !== contactPoolKeys.length) {
        state.contact_poll = {
          label: contactPollLabel,
          datasets: contactPollDatasets,
        };
      } else {
        state.contact_poll = undefined;
      }
    }

    if (action.payload?.voted) {
      const votedBar = action.payload.voted;
      let count = 0;
      const votedBarKeys = Object.keys(votedBar);
      votedBarKeys.forEach(key => {
        if (votedBar[key] === 0) {
          count++;
        }
      });

      if (count !== votedBarKeys.length) {
        state.voted = {
          label: Object.keys(action.payload.voted),
          datasets: Object.values(action.payload.voted),
        };
      } else {
        state.voted = undefined;
      }
    }

    if (action.payload?.voter_likelihood_bar) {
      const voterLikelihoodBar = action.payload.voter_likelihood_bar;

      const voterLikelihoodBarKeys = Object.keys(voterLikelihoodBar).map(key => {
        return [key, voterLikelihoodBar[key]];
      });

      const voterLikelihoodBarData = {};
      if (voterLikelihoodBarKeys.length) {
        const values = ['Expected', 'Highly Likely', 'Likely', 'Possible', 'Unlikely', 'Highly Unlikely', 'Skip'];
        values.forEach(val => {
          if (has(voterLikelihoodBar, val)) {
            voterLikelihoodBarData[val] = voterLikelihoodBar[val] || 0;
          }
        });
      }

      state.voterLikelihoodBar = {
        label: Object.keys(voterLikelihoodBarData),
        datasets: Object.values(voterLikelihoodBarData),
      };
    }

    if (action.payload?.city_council) {
      const cityCouncilObj = action.payload.city_council;
      const sortedCityCouncilObj = {} as ChartDataType;
      Object.keys(cityCouncilObj)
        .sort((a, b) => cityCouncilObj[b] - cityCouncilObj[a])
        .map(item => (sortedCityCouncilObj[item] = cityCouncilObj[item]));

      const labels = Object.keys(sortedCityCouncilObj).map(label => {
        if (label === '') {
          return 'N/A';
        }
        if (label === 'Precinct Others') {
          return 'Others';
        }
        return label;
      });
      const datasets = Object.values(sortedCityCouncilObj);
      state.cityCouncil = {
        label: labels,
        datasets: datasets,
      };
    }

    // --- Expected Remaining Voters chart: commented out 2026-03-12 ---
    // Replaced by Voter History Chart per customer request (Dec 2025).
    // This block mapped party names (Strong Republican, etc.) to voter counts.
    // Restore this block and the state field if this chart is ever needed again.
    // if (action.payload?.expected_remaining_voters_chart) {
    //   const expectedRemainingVoters: Record<string, number> = {
    //     [Party.STRONG_REPUBLICAN]: 0,
    //     [Party.SOFT_REPUBLICAN]: 0,
    //     [Party.STRONG_DEMOCRAT]: 0,
    //     [Party.SOFT_DEMOCRAT]: 0,
    //     [Party.SWING]: 0,
    //   };
    //   expectedRemainingVoters[Party.STRONG_REPUBLICAN] =
    //     action.payload.expected_remaining_voters_chart[Party.STRONG_REPUBLICAN] || 0;
    //   expectedRemainingVoters[Party.SOFT_REPUBLICAN] =
    //     action.payload.expected_remaining_voters_chart[Party.SOFT_REPUBLICAN] || 0;
    //   expectedRemainingVoters[Party.STRONG_DEMOCRAT] =
    //     action.payload.expected_remaining_voters_chart[Party.STRONG_DEMOCRAT] || 0;
    //   expectedRemainingVoters[Party.SOFT_DEMOCRAT] =
    //     action.payload.expected_remaining_voters_chart[Party.SOFT_DEMOCRAT] || 0;
    //   expectedRemainingVoters[Party.SWING] = action.payload.expected_remaining_voters_chart[Party.SWING] || 0;
    //   state.expected_remaining_voters_chart = expectedRemainingVoters;
    // }
    // --- End Expected Remaining Voters ---

    // Voter History Chart — pass through directly from API response
    // Cast needed: installed SDK type may not yet include 'years' field
    if (action.payload?.voter_history_chart) {
      state.voter_history_chart = action.payload.voter_history_chart as VoterHistoryChartData;
    }

    if (action.payload?.preferred_voting_style_bar) {
      state.preferredVotingStyleBar = {
        label: Object.keys(action.payload.preferred_voting_style_bar),
        datasets: Object.values(action.payload.preferred_voting_style_bar),
      };
    }

    if (action.payload?.projected_outcomes_chart) {
      const projectedOutcomesChart = action.payload.projected_outcomes_chart;

      const projectedOutcomesArray = Object.keys(projectedOutcomesChart).map(key => {
        return [key, projectedOutcomesChart[key]];
      });

      const renderArray = [] as any;
      projectedOutcomesArray.forEach(item => {
        if (item[0] === 'Likely Opponents') {
          renderArray[0] = item;
        }
        if (item[0] === 'Unknown') {
          renderArray[1] = item;
        }
        if (item[0] === 'Likely Supporters') {
          renderArray[2] = item;
        }
      });

      const projectedOutcomesChartLabel = renderArray.map(array => array[0]);
      const projectedOutcomesChartDatasets = renderArray.map(array => array[1]);

      state.projected_outcomes_chart = {
        label: projectedOutcomesChartLabel,
        datasets: projectedOutcomesChartDatasets,
      };
    }

    if (action.payload?.county_dropdown) {
      state.countyDropdown = action.payload.county_dropdown;
    }

    if (action.payload?.precinct_dropdown) {
      state.precinctDropdown = action.payload.precinct_dropdown;
    }

    if (action.payload?.select_control_county_dropdown) {
      state.selectControlCountyDropdown = action.payload.select_control_county_dropdown;
    }

    if (action.payload?.select_control_precinct_dropdown) {
      state.selectControlPrecinctDropdown = action.payload.select_control_precinct_dropdown;
    }

    if (
      action.payload?.tag_dropdown &&
      (!state.changedKeys.includes('tags_dropdown') ||
        (state.changedKeys.includes('tags_dropdown') && !state.dashboardFilterParams.tags_dropdown))
    ) {
      state.tagDropdown = action.payload.tag_dropdown;
    }

    if (action.payload?.tags) {
      const label = Object.keys(action.payload.tags);
      const datasets = Object.values(action.payload.tags);
      if (label.length > 5) {
        label[4] = 'Others';
        datasets[4] = datasets.splice(4).reduce((prev, next) => prev + next);

        label.splice(5, label.length);
        datasets.splice(5, datasets.length);
      }

      state.selectedTags = {
        label,
        datasets,
      };
    }

    if (action.payload?.survey_chart) {
      const surveyChart = action.payload.survey_chart;
      const result: SurveyChartDataType = {};

      for (const surveyId in surveyChart) {
        const questions = surveyChart[surveyId];
        result[surveyId] = {};

        for (const questionId in questions) {
          const selectedOptions = questions[questionId].selected_options;
          result[surveyId][questionId] = selectedOptions;
        }
      }

      state.survey_chart = result;
    }

    if (state.filterParam && state.isFilterChartsByChart && !state.isClearFilter) {
      state[state.filterParam.name] = state.filterParam.data;
    }
  }

  state.countLoading--;
  state.firstLoad = false;
};
